"use client";

import { useSession } from "next-auth/react";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useMemo, useRef } from "react";
import {
  capturePostHogPageView,
  getPostHogDistinctId,
  identifyPostHogUser,
  initPostHog,
  resetPostHog,
} from "@/utils/posthog";

const PostHogClientEffects = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data: session, status } = useSession();
  const lastPageViewUrlRef = useRef("");
  const identifiedUserRef = useRef("");

  const routeKey = useMemo(() => {
    const queryString = searchParams?.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
  }, [pathname, searchParams]);

  useEffect(() => {
    void initPostHog();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !routeKey) return;

    const currentUrl = `${window.location.origin}${routeKey}`;
    if (lastPageViewUrlRef.current === currentUrl) return;

    lastPageViewUrlRef.current = currentUrl;
    void capturePostHogPageView({
      current_url: currentUrl,
      referrer: document.referrer || undefined,
    });
  }, [routeKey]);

  useEffect(() => {
    const user = session?.user as Record<string, unknown> | undefined;
    const distinctId = getPostHogDistinctId(user);

    if (status === "authenticated" && distinctId && identifiedUserRef.current !== distinctId) {
      identifiedUserRef.current = distinctId;
      void identifyPostHogUser(user);
      return;
    }

    if (status === "unauthenticated" && identifiedUserRef.current) {
      identifiedUserRef.current = "";
      void resetPostHog();
    }
  }, [session?.user, status]);

  return null;
};

const PostHogClientProvider = () => (
  <Suspense fallback={null}>
    <PostHogClientEffects />
  </Suspense>
);

export default PostHogClientProvider;
